/**
 * Thermometers Page - HAL 3000 - iPad Version
 * Style AFFICHAGE LED INDUSTRIEL - OCCUPE TOUT L'ESPACE
 * 16 capteurs en grille 4x4 avec groupes visuels
 */
import { useState, useEffect, useCallback } from 'react';
import Layout from '@/components/Layout';
import { api } from '@/App';
import { RefreshCw, Battery, BatteryLow, BatteryMedium, BatteryFull, BatteryWarning } from 'lucide-react';

// Couleur LED selon température
const getTempColor = (temp) => {
  if (temp === null || temp === undefined) return '#333';
  if (temp < 15) return '#3b82f6'; // bleu - froid (< 15°C)
  if (temp < 21) return '#22c55e'; // vert - confort (15-21°C)
  if (temp <= 25) return '#f97316'; // orange - chaud (21-25°C)
  return '#ef4444'; // rouge - trop chaud (> 25°C)
};

// Icône et couleur batterie selon niveau
const getBatteryInfo = (level) => {
  if (level === null || level === undefined) return { icon: Battery, color: '#555', label: '--' };
  if (level <= 20) return { icon: BatteryWarning, color: '#ef4444', label: level + '%' }; // Rouge critique
  if (level <= 40) return { icon: BatteryLow, color: '#f97316', label: level + '%' }; // Orange bas
  if (level <= 70) return { icon: BatteryMedium, color: '#eab308', label: level + '%' }; // Jaune moyen
  return { icon: BatteryFull, color: '#22c55e', label: level + '%' }; // Vert plein
};

// Étiquette DYMO - Style embossé réaliste (fond noir, texte blanc en relief)
const DymoLabel = ({ text }) => (
  <div 
    className="px-1 py-0.5 text-center w-full"
    style={{
      background: '#000',
      borderRadius: '2px',
      border: '1px solid #222',
      boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.8)'
    }}
  >
    <span
      style={{
        fontFamily: "'Arial Narrow', 'Helvetica Neue', Arial, sans-serif",
        fontWeight: '800',
        fontSize: '10px',
        color: '#fff',
        letterSpacing: '1px',
        textTransform: 'uppercase',
        textShadow: '1px 1px 0 #333, -1px -1px 0 #111, 0 0 6px rgba(255,255,255,0.3)'
      }}
    >
      {text}
    </span>
  </div>
);

// Indicateur de capteur avec bord métal - COMPACT pour grille 4x4
const SensorPanel = ({ sensor, isEmpty = false, isExterior = false, onRefresh = null }) => {
  const [blinkState, setBlinkState] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  
  // Couleurs par groupe
  const GROUP_COLORS = {
    'st_louis': '#a855f7',      // Violet
    'petite_maison': '#22c55e', // Vert
    'jerusalem': '#f59e0b',     // Orange
    'jeanne_arc': '#ec4899',    // Rose
    'other': '#4a4a4a'          // Gris par défaut
  };
  
  // Clignotement pour alertes
  useEffect(() => {
    const needsBlink = !isEmpty && sensor && (
      !sensor.online || 
      (sensor.temperature !== null && sensor.temperature > 25) || 
      (sensor.temperature !== null && sensor.temperature < 9) || 
      (sensor.battery !== null && sensor.battery > 0 && sensor.battery <= 20)
    );
    if (needsBlink) {
      const interval = setInterval(() => setBlinkState(prev => !prev), 500);
      return () => clearInterval(interval);
    }
  }, [sensor, isEmpty]);

  const handleClick = async () => {
    if (!onRefresh || isEmpty || isRefreshing) return;
    setIsRefreshing(true);
    await onRefresh(sensor.id);
    setTimeout(() => setIsRefreshing(false), 1000);
  };

  if (isEmpty) {
    return (
      <div 
        className="flex flex-col items-center justify-center p-1 rounded-lg"
        style={{
          background: 'linear-gradient(180deg, #0a0a0a 0%, #050505 100%)',
          border: '3px solid #3a3a3a',
          boxShadow: 'inset 0 2px 6px rgba(0,0,0,0.9), 0 2px 4px rgba(0,0,0,0.5)'
        }}
      >
        <div className="text-4xl font-mono text-gray-800" style={{ letterSpacing: '-2px' }}>--.-</div>
        <div className="text-2xl font-mono text-gray-800">--%</div>
        <div className="mt-1 w-full">
          <DymoLabel text="LIBRE" />
        </div>
      </div>
    );
  }

  const isOnline = sensor.online;
  // Garage/extérieur = teinte bleue différente
  const tempColor = isExterior ? '#06b6d4' : getTempColor(sensor.temperature);
  const humidityColor = '#00d4ff'; // Bleu électrique
  
  // Couleur du groupe
  const groupColor = GROUP_COLORS[sensor.group] || GROUP_COLORS['other'];
  
  // Alertes clignotantes
  const isTooHot = sensor.temperature !== null && sensor.temperature > 25;
  const isTooCold = sensor.temperature !== null && sensor.temperature < 9;
  const isBatteryLow = sensor.battery !== null && sensor.battery > 0 && sensor.battery <= 20;
  
  // Couleur de bordure selon état (alertes prioritaires sur groupe)
  let borderColor = groupColor;
  let glowColor = `0 0 8px ${groupColor}40`;
  if (!isOnline) {
    borderColor = blinkState ? '#ef4444' : groupColor;
    glowColor = blinkState ? '0 0 15px #ef444480' : `0 0 8px ${groupColor}40`;
  } else if (isTooHot) {
    borderColor = blinkState ? '#ef4444' : groupColor;
    glowColor = blinkState ? '0 0 20px #ef444480' : `0 0 8px ${groupColor}40`;
  } else if (isTooCold) {
    borderColor = blinkState ? '#3b82f6' : groupColor;
    glowColor = blinkState ? '0 0 20px #3b82f680' : `0 0 8px ${groupColor}40`;
  } else if (isBatteryLow) {
    borderColor = blinkState ? '#ef4444' : groupColor;
    glowColor = blinkState ? '0 0 15px #ef444480' : `0 0 8px ${groupColor}40`;
  }

  return (
    <div 
      className="relative flex flex-col items-center justify-between p-2 rounded-lg transition-all cursor-pointer hover:scale-[1.02]"
      style={{
        background: isOnline 
          ? 'linear-gradient(180deg, #0f0f0f 0%, #050505 100%)' 
          : 'linear-gradient(180deg, #0a0303 0%, #030101 100%)',
        border: `3px solid ${borderColor}`,
        boxShadow: `inset 0 2px 6px rgba(0,0,0,0.9), 0 2px 4px rgba(0,0,0,0.5), ${glowColor}`,
        opacity: isRefreshing ? 0.7 : 1
      }}
      onClick={handleClick}
      title="Cliquer pour rafraîchir"
      data-testid={`sensor-${sensor.id}`}
    >
      {/* Indicateur de refresh */}
      {isRefreshing && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-lg z-20">
          <div className="w-6 h-6 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin"></div>
        </div>
      )}
      {/* HORS LIGNE indicator - EN ROUGE SUR LE CAPTEUR */}
      {!isOnline && (
        <div 
          className="absolute top-1 left-1/2 -translate-x-1/2 px-2 py-0.5 rounded text-xs font-bold z-10"
          style={{ 
            background: blinkState ? '#dc2626' : '#7f1d1d',
            color: '#fff',
            border: '1px solid #ef4444',
            boxShadow: blinkState ? '0 0 8px #ef4444' : 'none'
          }}
        >
          HORS LIGNE
        </div>
      )}
      
      {/* MOCK indicator (capteur miroir d'un autre) */}
      {sensor.mocked && (
        <div
          className="absolute top-1 right-1 px-1.5 py-0.5 rounded text-[8px] font-bold z-10"
          style={{
            background: '#1e3a8a',
            color: '#93c5fd',
            border: '1px solid #3b82f6',
            letterSpacing: '0.5px',
          }}
          title={sensor.mirror_of ? `Données miroir de "${sensor.mirror_of}"` : 'Capteur mocké'}
          data-testid={`mock-badge-${sensor.id}`}
        >
          MOCK
        </div>
      )}
      
      {/* SECTION HAUTE: Température + Humidité (position fixe) */}
      <div className={`flex flex-col items-center ${!isOnline ? 'mt-4' : ''}`}>
        {/* Température LED - GROS CHIFFRES sans °C */}
        <div 
          className="text-5xl font-bold font-mono leading-none"
          style={{ 
            color: isOnline ? tempColor : '#555',
            textShadow: isOnline ? `0 0 20px ${tempColor}, 0 0 40px ${tempColor}80` : 'none',
            fontFamily: "'Courier New', 'Lucida Console', monospace",
            letterSpacing: '-3px',
            opacity: !isOnline ? 0.5 : 1
          }}
        >
          {sensor.temperature !== null && sensor.temperature !== undefined && typeof sensor.temperature === 'number' ? sensor.temperature.toFixed(1) : '--'}
        </div>
        
        {/* Humidité en BLEU ÉLECTRIQUE - GROS */}
        <div 
          className="text-3xl font-bold font-mono leading-none"
          style={{ 
            color: isOnline ? humidityColor : '#333',
            textShadow: isOnline ? `0 0 15px ${humidityColor}80` : 'none',
            opacity: !isOnline ? 0.5 : 1
          }}
        >
          {sensor.humidity !== null ? sensor.humidity : '--'}%
        </div>
      </div>
      
      {/* SECTION BASSE: Nom + Batterie (descend en bas) */}
      <div className="flex flex-col items-center w-full mt-auto">
        {/* Nom DYMO - COMPACT */}
        <div className="w-full">
          <DymoLabel text={(sensor.name || '').replace(/Capteur de température\s*/gi, '').toUpperCase()} />
        </div>
        
        {/* Indicateur Batterie - TOUJOURS AFFICHÉ si données disponibles */}
        {sensor.battery !== null && sensor.battery > 0 && isOnline && (() => {
          const batteryInfo = getBatteryInfo(sensor.battery);
          const BatteryIcon = batteryInfo.icon;
          const isCritical = sensor.battery <= 20;
          return (
            <div 
              className="mt-0.5 flex items-center gap-0.5"
              style={{ 
                opacity: isCritical && blinkState ? 1 : 0.9
              }}
            >
              <BatteryIcon 
                className="w-3 h-3" 
                style={{ 
                  color: isCritical && blinkState ? '#fca5a5' : batteryInfo.color,
                  filter: isCritical ? `drop-shadow(0 0 3px ${batteryInfo.color})` : 'none'
                }} 
              />
              <span 
                className="text-[9px] font-mono font-bold"
                style={{ 
                  color: isCritical && blinkState ? '#fca5a5' : batteryInfo.color
                }}
              >
                {batteryInfo.label}
              </span>
            </div>
          );
        })()}
      </div>
    </div>
  );
};

const ThermometersPage = () => {
  const [sensors, setSensors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const loadSensors = useCallback(async () => {
    try {
      // NOUVEL ENDPOINT OPTIMISÉ: températures en temps réel avec cache 30min
      const response = await api.get('/integrations/tuya/temperature-sensors');
      
      if (response.data?.success) {
        let tempSensors = response.data.sensors || [];
        
        // ID du capteur problématique "Salle de bains Saint Louis de Gonzague"
        const MISSING_SENSOR_ID = 'bff73b5baac3fa5a38drfr';
        
        // Vérifier si le capteur problématique est présent avec des données
        const missingSensor = tempSensors.find(d => d.id === MISSING_SENSOR_ID);
        const hasMissingSensorData = missingSensor?.temperature !== null;
        
        // Si le capteur n'a pas de données, récupérer via l'API details
        let extraSensor = null;
        if (!hasMissingSensorData) {
          try {
            const detailsResp = await api.get(`/integrations/tuya/devices/${MISSING_SENSOR_ID}/details`);
            if (detailsResp.data?.success && detailsResp.data?.properties?.properties) {
              const props = detailsResp.data.properties.properties;
              const tempProp = props.find(p => p.code === 'temp_current');
              const humProp = props.find(p => p.code === 'humidity_value');
              const battProp = props.find(p => p.code === 'battery_state');
              
              if (tempProp) {
                extraSensor = {
                  id: MISSING_SENSOR_ID,
                  name: 'Salle de bains St Louis de Gonzague',
                  online: true,
                  temperature: tempProp.value / 10,
                  humidity: humProp?.value || null,
                  battery: battProp?.value === 'high' ? 100 : battProp?.value === 'middle' ? 50 : battProp?.value === 'low' ? 10 : null,
                  lastUpdate: tempProp.time
                };
              }
            }
          } catch (e) {
            console.log('Could not fetch extra sensor details:', e);
          }
        }
        
        // Ajouter le capteur récupéré via API v2.0 s'il existe et n'est pas déjà présent
        if (extraSensor) {
          const alreadyExists = tempSensors.some(s => 
            s.id === extraSensor.id || 
            s.name.toLowerCase().replace(/[()]/g, '').replace(/\s+/g, ' ').trim() === 
            extraSensor.name.toLowerCase().replace(/[()]/g, '').replace(/\s+/g, ' ').trim()
          );
          if (!alreadyExists) {
            tempSensors.push(extraSensor);
          }
        }
        
        // Filtrer les capteurs invalides (id manquant, name null) qui crashent l'UI
        tempSensors = (tempSensors || []).filter(s => s && s.id && (s.name || s.tuya_name));
        // S'assurer que name est toujours une chaîne
        tempSensors = tempSensors.map(s => ({ ...s, name: String(s.name || s.tuya_name || s.id) }));
        
        // === ROUTINE AUTO-CONTRÔLE ET CORRECTION TEMPÉRATURES ===
        // Étape 1: Collecter les températures "normales" pour calculer la médiane de référence
        const normalTemps = tempSensors
          .filter(s => s.temperature !== null && s.temperature !== undefined && typeof s.temperature === 'number' && s.temperature >= 5 && s.temperature <= 35)
          .map(s => s.temperature);
        
        let median = 18; // Valeur par défaut si pas assez de données
        if (normalTemps.length >= 3) {
          normalTemps.sort((a, b) => a - b);
          median = normalTemps[Math.floor(normalTemps.length / 2)];
        }
        
        // Étape 2: Corriger les valeurs aberrantes
        tempSensors.forEach(sensor => {
          if (sensor.temperature !== null && typeof sensor.temperature === 'number') {
            const originalTemp = sensor.temperature;
            const sensorName = (sensor.name || '').toLowerCase();
            const isExterior = sensorName.includes('garage') || sensorName.includes('atelier');
            const minPlausible = isExterior ? -10 : 5;
            const maxPlausible = isExterior ? 45 : 35;
            
            // Cas 1: Valeur trop haute (ex: 90°C) → probablement manque division par 10
            if (originalTemp > maxPlausible) {
              const corrected = originalTemp / 10;
              if (corrected >= minPlausible && corrected <= maxPlausible && Math.abs(corrected - median) < 20) {
                console.log(`[HAL] Correction température: ${sensor.name} ${originalTemp}°C → ${corrected.toFixed(1)}°C (÷10)`);
                sensor.temperature = corrected;
                sensor.corrected = true;
              } else {
                const corrected100 = originalTemp / 100;
                if (corrected100 >= minPlausible && corrected100 <= maxPlausible) {
                  console.log(`[HAL] Correction température: ${sensor.name} ${originalTemp}°C → ${corrected100.toFixed(1)}°C (÷100)`);
                  sensor.temperature = corrected100;
                  sensor.corrected = true;
                }
              }
            }
            
            // Cas 2: Valeur trop basse mais positive (ex: 1.6°C au lieu de 16°C)
            else if (originalTemp > 0 && originalTemp < minPlausible) {
              const corrected = originalTemp * 10;
              if (corrected >= minPlausible && corrected <= maxPlausible && Math.abs(corrected - median) < 20) {
                console.log(`[HAL] Correction température: ${sensor.name} ${originalTemp}°C → ${corrected.toFixed(1)}°C (×10)`);
                sensor.temperature = corrected;
                sensor.corrected = true;
              }
            }
            
            // Cas 3: Valeur négative aberrante (ex: -50°C)
            else if (originalTemp < -20) {
              const absTemp = Math.abs(originalTemp);
              if (absTemp >= minPlausible && absTemp <= maxPlausible) {
                console.log(`[HAL] Correction température: ${sensor.name} ${originalTemp}°C → ${absTemp}°C (|abs|)`);
                sensor.temperature = absTemp;
                sensor.corrected = true;
              } else if (absTemp / 10 >= minPlausible && absTemp / 10 <= maxPlausible) {
                console.log(`[HAL] Correction température: ${sensor.name} ${originalTemp}°C → ${(absTemp/10).toFixed(1)}°C (|abs|÷10)`);
                sensor.temperature = absTemp / 10;
                sensor.corrected = true;
              }
            }
            
            // Log si toujours aberrant après corrections
            if (sensor.temperature < -20 || sensor.temperature > 50) {
              console.warn(`[HAL] Température incorrigible: ${sensor.name} = ${sensor.temperature}°C`);
              sensor.temperature = null;
              sensor.suspect = true;
            }
          }
        });
        
        // NE PAS TRIER - L'ordre est géré par le backend (groupes puis alphabétique)
        // Le backend retourne déjà: St Louis > Petite Maison > Jérusalem > Jeanne d'Arc > Autres
        
        setSensors(tempSensors);
      }
    } catch (error) {
      console.error('Error loading sensors:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSensors();
    // POLLING 30 MINUTES - STRICT pour économiser le quota Tuya Cloud (30k appels/mois)
    // Les capteurs WiFi ne supportent PAS le mode local (ils dorment 99% du temps)
    const interval = setInterval(loadSensors, 1800000); // 30 minutes = 1800000ms
    return () => clearInterval(interval);
  }, [loadSensors]);

  // Refresh global (bouton)
  const handleRefresh = async () => {
    setRefreshing(true);
    await loadSensors();
    setTimeout(() => setRefreshing(false), 500);
  };

  // Refresh individuel d'un capteur (clic sur le panneau)
  const handleSensorRefresh = useCallback(async (deviceId) => {
    try {
      // Forcer un refresh depuis l'API Tuya pour ce device spécifique
      await api.get(`/integrations/tuya/devices/${deviceId}/status`);
      // Puis recharger tous les capteurs pour mettre à jour l'affichage
      await loadSensors();
    } catch (error) {
      console.error('Error refreshing sensor:', error);
      // Fallback: refresh global
      await loadSensors();
    }
  }, [loadSensors]);

  // Calculs moyennes — uniquement les capteurs avec température numérique valide
  const onlineSensors = sensors.filter(s => s && s.online && typeof s.temperature === 'number' && !isNaN(s.temperature));
  
  // Identifier l'atelier/garage (température extérieure) - exclu des moyennes
  const isGarageSensor = (name) => {
    const n = (name || '').toLowerCase();
    return n.includes('atelier') || n.includes('garage') || n.includes('ext');
  };
  
  // Pièces chauffées = toutes sauf garage/atelier
  const heatedRoomSensors = onlineSensors.filter(s => !isGarageSensor(s.name));
  // Pièces non chauffées = tout sauf garage (pour moyenne générale sans extrêmes)
  const generalSensors = onlineSensors.filter(s => !isGarageSensor(s.name));
  
  // Moyenne des pièces chauffées
  const avgTempHeated = heatedRoomSensors.length > 0 
    ? heatedRoomSensors.reduce((sum, s) => sum + s.temperature, 0) / heatedRoomSensors.length 
    : null;
  
  // Moyenne générale (sans garage)
  const avgTempGeneral = generalSensors.length > 0 
    ? generalSensors.reduce((sum, s) => sum + s.temperature, 0) / generalSensors.length 
    : null;
  
  // Température garage/extérieur
  const garageSensor = onlineSensors.find(s => isGarageSensor(s.name));
  const garageTemp = garageSensor?.temperature || null;
  
  // Utiliser avgTempHeated comme moyenne principale
  const avgTemp = avgTempHeated;
  
  const avgHumidity = onlineSensors.filter(s => s.humidity !== null).length > 0
    ? onlineSensors.filter(s => s.humidity !== null).reduce((sum, s) => sum + s.humidity, 0) / onlineSensors.filter(s => s.humidity !== null).length
    : null;

  // Créer la grille 4x4 = 16 slots (14 capteurs + 2 mockés)
  const TOTAL_SLOTS = 16; // 4 colonnes × 4 lignes
  const emptySlots = Math.max(0, TOTAL_SLOTS - sensors.length);

  return (
    <Layout title="Capteurs Température">
      <div className="h-full flex flex-col overflow-hidden" style={{ background: '#000' }}>
        
        {/* HEADER COMPACT */}
        <div 
          className="flex items-center justify-between px-4 py-2 flex-shrink-0"
          style={{ background: '#0a0a0a', borderBottom: '2px solid #333' }}
        >
          <div className="flex items-center gap-6">
            <DymoLabel text="SURVEILLANCE THERMIQUE" />
            <div className="flex items-center gap-4 font-mono text-sm">
              <span className="text-gray-400">{sensors.length} CAPTEURS</span>
              <span className="text-green-400">{sensors.filter(s => s.online).length} EN LIGNE</span>
              {sensors.filter(s => !s.online).length > 0 && (
                <span className="text-red-400">{sensors.filter(s => !s.online).length} HORS LIGNE</span>
              )}
            </div>
          </div>
          
          {/* MOYENNE MAISON dans le header */}
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-3 px-4 py-1 rounded" style={{ background: '#111', border: '2px solid #333' }}>
              <span className="text-gray-400 font-mono text-sm">MOY:</span>
              <span 
                className="text-3xl font-bold font-mono"
                style={{ 
                  color: getTempColor(avgTemp),
                  textShadow: `0 0 15px ${getTempColor(avgTemp)}`
                }}
              >
                {avgTemp?.toFixed(1) || '--'}°
              </span>
              <span 
                className="text-2xl font-bold font-mono"
                style={{ color: '#00d4ff', textShadow: '0 0 15px #00d4ff' }}
              >
                {avgHumidity?.toFixed(0) || '--'}%
              </span>
            </div>
            
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              data-testid="refresh-sensors"
              className="p-2 rounded"
              style={{ background: '#0a2030', border: '2px solid #1a4060' }}
            >
              <RefreshCw className={`w-5 h-5 text-cyan-400 ${refreshing ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        {/* GRILLE DE CAPTEURS - OCCUPE TOUT L'ESPACE */}
        <div className="flex-1 p-3 overflow-hidden">
          {loading ? (
            <div className="h-full flex items-center justify-center">
              <div className="text-gray-500 font-mono text-2xl animate-pulse">CHARGEMENT...</div>
            </div>
          ) : sensors.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center px-6" data-testid="no-sensors-msg">
              <div className="text-yellow-400 font-mono text-2xl mb-2">⚠ AUCUN CAPTEUR</div>
              <div className="text-gray-400 font-mono text-sm mb-4 max-w-xl">
                Aucun capteur de température n'a été retourné par l'API.
                Cela peut signifier :
              </div>
              <ul className="text-gray-500 font-mono text-xs text-left list-disc list-inside space-y-1">
                <li>La base Tuya n'est pas encore synchronisée → allez dans <b>Admin → AUTO-DÉCOUVERTE</b></li>
                <li>Le service Tuya Cloud est temporairement indisponible</li>
                <li>Le quota Tuya est épuisé (vérifier <b>/api/integrations/tuya/quota</b>)</li>
              </ul>
              <button
                onClick={handleRefresh}
                className="mt-4 px-4 py-2 rounded bg-cyan-900 hover:bg-cyan-800 text-cyan-300 font-mono text-sm"
                data-testid="retry-sensors-btn"
              >
                RÉESSAYER
              </button>
            </div>
          ) : (
            <div 
              className="h-full grid gap-2"
              style={{ 
                gridTemplateColumns: 'repeat(4, 1fr)',
                gridTemplateRows: 'repeat(4, 1fr)'
              }}
            >
              {/* Tous les capteurs - garage/atelier marqué comme extérieur */}
              {sensors.map((sensor, idx) => {
                const n = (sensor.name || '').toLowerCase();
                // Seulement garage/atelier sont considérés "extérieurs", pas "Extension"
                const isExterior = (n.includes('atelier') || n.includes('garage')) && !n.includes('extension');
                return (
                  <SensorPanel 
                    key={sensor.id || `sensor-${idx}`} 
                    sensor={sensor} 
                    isExterior={isExterior} 
                    onRefresh={handleSensorRefresh}
                  />
                );
              })}
              
              {/* Emplacements vides */}
              {Array.from({ length: emptySlots }).map((_, i) => (
                <SensorPanel key={`empty-${i}`} isEmpty />
              ))}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default ThermometersPage;
